import React, { useCallback, useState, useMemo } from 'react';

import SetToManualFlow from 'modules/set-to-manual-flow';

import { unlockShipperOrder, unlockJourney } from './services';

import * as http from 'utils/http';
import * as itineraryHttp from 'modules/assignment-map/http';

import { moveItineraryToManual } from 'modules/dispatching/service';

const SetToManualFlowContext = React.createContext();

function SetToManualFlowContextProvider({ children, model = 'shipperOrder' }) {
  const [openedId, setOpenedId] = useState(null);
  const [callbacks, setCallbacks] = useState(null);

  const unlockRequest = useMemo(
    () => ({
      shipperOrder: unlockShipperOrder,
      journey: unlockJourney,
      itinerary: moveItineraryToManual,
    }),
    []
  );

  const openModal = useCallback((id, callbacks) => {
    setOpenedId(id);
    setCallbacks({
      beforeSubmit: callbacks?.beforeSubmit,
      afterSubmit: callbacks?.afterSubmit,
    });
  }, []);

  const closeModal = useCallback(() => {
    setOpenedId(null);
    setCallbacks(null);
  }, []);

  const handleSubmit = useCallback(
    async ({ reason, comment }) => {
      !!callbacks?.beforeSubmit && callbacks.beforeSubmit();
      try {
        setOpenedId(null);
        await unlockRequest[model](openedId, {
          unlock_reason: reason,
          unlock_comment: comment,
        });
      } catch (err) {
        console.error(await http.getError(err));
      } finally {
        !!callbacks?.afterSubmit && callbacks.afterSubmit();
        setCallbacks(null);
      }
    },
    [callbacks, openedId, unlockRequest, model]
  );

  const unlock = useCallback(
    async (id) => {
      try {
        await unlockRequest[model](id, { unlock_reason: '', unlock_comment: '' });
      } catch (err) {
        console.error(await http.getError(err));
      }
    },
    [model, unlockRequest]
  );

  const needsManualReasonsModal = async (id) => {
    const withManualReasonsModal = ['FR'];
    const itinerary = await itineraryHttp.getItinerary(id);
    const country = itinerary.stops[0].address.country;
    return withManualReasonsModal.indexOf(country) >= 0;
  };

  const value = {
    openModal,
    closeModal,
    unlock,
    needsManualReasonsModal,
  };

  return (
    <SetToManualFlowContext.Provider value={value}>
      {children}
      <SetToManualFlow
        onClose={closeModal}
        opened={!!openedId}
        onSubmit={handleSubmit}
        model={model}
      />
    </SetToManualFlowContext.Provider>
  );
}

export default SetToManualFlowContext;
export { SetToManualFlowContextProvider };
